import React, {FC, useState, useEffect, useMemo} from 'react'
import {Dropdown, Form} from 'react-bootstrap'
import Select, {MultiValue} from 'react-select'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import {useAuth} from '../../../../modules/auth'
import {KTSVG} from '../../../../../_metronic/helpers'
import {CommonLoading} from '../../../../../Utils/commonLoading'
import {Formik} from 'formik'
import {useDispatch, useSelector} from 'react-redux'
import {
  InterpreterUnInvoicedCallsCurrentPage,
  InterpreterUnInvoicedCallsFilter,
  InterpreterUnInvoicedCallsRowsPerPage,
} from '../../../../redux/tableSlice/tableSlice'
import {useInterpreters, useLanguages, useCommunicationTypes} from '../../../../hooks/useGlobalData'
import LazySelect from '../../../../components/LazySelect'

type Props = {
  setCurrentPage: any
  fetchcall_log: any
  searchQuery: any
  filterData: any
}

export function InterperterFilterDropdown({
  setCurrentPage,
  fetchcall_log,
  searchQuery,
  filterData,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const {currentUser} = useAuth()
  const dispatch = useDispatch()
  const {rowsPerPage} = useSelector((state: any) => {
    return {
      rowsPerPage: state.table.InterpreterUnInvoicedCalls[0].rowsPerPage,
    }
  })
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const [isFilterApplied, setIsFilterApplied] = useState(false)

  // Use global cached data for interpreters, languages, and communication types
  const {data: interpretersData, isLoading: isFetchingInterpreter} = useInterpreters()
  const {data: languagesData, isLoading: isFetchingNativeLanguage} = useLanguages()
  const {data: communicationTypesData, isLoading: isFetchingCommunication} = useCommunicationTypes()

  useEffect(() => {
    const hasFilters =
      filterData?.awsUserId_INTList?.length > 0 ||
      filterData?.languageList?.length > 0 ||
      filterData?.communicationTypes?.length > 0 ||
      filterData?.startDate ||
      filterData?.endDate ||
      filterData?.appointmentStatusList?.length > 0 ||
      filterData?.meetingSummaryStatusList?.length > 0
    setIsFilterApplied(hasFilters)
  }, [filterData])

  const languageOptions = useMemo(() => 
    languagesData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [languagesData])

  const requesterOptions = useMemo(() => 
    interpretersData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [interpretersData])

  const communicationTypeOptions = useMemo(() => 
    communicationTypesData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [communicationTypesData])

  const communicationOptions = communicationTypeOptions

  const appointmentStatusOptions = [
    {value: '0', label: 'Open'},
    {value: '1', label: 'Ready to Assign'},
    {value: '2', label: 'Confirmed'},
    {value: '3', label: 'No Interpreter'},
    {value: '4', label: 'Abandoned'},
    {value: '5', label: 'Completed'},
    {value: '6', label: 'Canceled'},
    {value: '7', label: 'Submitted'},
  ]

  const meetingSummaryStatusOptions = [
    {value: '0', label: 'New'},
    {value: '1', label: 'Pending'},
    {value: '2', label: 'Approved'},
    {value: '3', label: 'Rejected'},
    {value: '-1', label: 'N/A'},
  ]

  const handleReset = async () => {
    dispatch(InterpreterUnInvoicedCallsCurrentPage(1))
    dispatch(InterpreterUnInvoicedCallsRowsPerPage(10))
    dispatch(
      InterpreterUnInvoicedCallsFilter({
        awsUserId_INTList: [],
        communicationTypes: [],
        languageList: [],
        startDate: null,
        endDate: null,
        callType: 50,
        appointmentStatusList: [],
        meetingSummaryStatusList: [],
      })
    )
    setShowDropdownMenu(false)
    await fetchcall_log(
      {
        awsUserId_INTList: [],
        languageList: [],
        startDate: null,
        communicationTypes: [],
        endDate: null,
        companyCode: null,
        callType: 50,
        appointmentStatusList: [],
        meetingSummaryStatusList: [],
      },
      searchQuery,
      1,
      10
    )
    setIsFilterApplied(false)
  }

  /******  b2da24b8-a3f9-475d-8c0a-943c73758816  *******/
  return (
    <>
      <Dropdown onToggle={(nextShow) => setShowDropdownMenu(nextShow)}>
        <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-filter'>Filter</Tooltip>}>
          <Dropdown.Toggle
            variant='primary'
            id='dropdown-basic'
            // Call the fetchcall_log function with default filters
            className={`btn btn-icon btn-sm no-caret ${isFilterApplied ? 'btn-light-danger' : ''}`}
          >
            <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-primary' />
          </Dropdown.Toggle>
        </OverlayTrigger>
        {showDropdownMenu && (
          <Formik
            enableReinitialize
            initialValues={{
              ...filterData,
              awsUserId_INTList:
                requesterOptions.filter((d: any) =>
                  filterData?.awsUserId_INTList?.includes(d?.value)
                ) ?? [],
              languageList:
                languageOptions.filter((d: any) => filterData?.languageList?.includes(d?.label)) ??
                [],
              communicationTypes:
                communicationOptions.filter((d: any) => filterData?.communicationTypes?.includes(d?.value)) ??
                [],
              appointmentStatusList:
                appointmentStatusOptions.filter((d: any) =>
                  filterData?.appointmentStatusList?.includes(d?.value)
                ) ?? [],
              meetingSummaryStatusList:
                meetingSummaryStatusOptions.filter((d: any) =>
                  filterData?.meetingSummaryStatusList?.includes(d?.value)
                ) ?? [],
            }}
            onSubmit={async (values, {setSubmitting, resetForm}) => {
              setSubmitting(true)
              const passingData = {
                awsUserId_INTList: values?.awsUserId_INTList?.map((option: any) => option.value) || [],
                languageList: values?.languageList?.map((option: any) => option.label) || [],
                communicationTypes: values?.communicationTypes?.map((option: any) => option.value) || [],
                startDate: values.startDate || undefined,
                endDate: values.endDate || undefined,
                callType: 50,
                appointmentStatusList: values?.appointmentStatusList?.map((option: any) => option.value) || [],
                meetingSummaryStatusList: values?.meetingSummaryStatusList?.map((option: any) => option.value) || [],
              }
              try {
                dispatch(InterpreterUnInvoicedCallsCurrentPage(1))
                dispatch(InterpreterUnInvoicedCallsFilter({...passingData}))
                await fetchcall_log(passingData, searchQuery, 1, rowsPerPage)
                const hasFilters =
                  passingData.awsUserId_INTList?.length > 0 ||
                  passingData.languageList?.length > 0 ||
                  passingData.communicationTypes?.length > 0 ||
                  passingData.startDate ||
                  passingData.endDate ||
                  passingData.appointmentStatusList?.length > 0 ||
                  passingData.meetingSummaryStatusList?.length > 0
                setIsFilterApplied(hasFilters)
              } catch (error) {
                console.error(error)
              } finally {
                setShowDropdownMenu(false)
                setIsLoading(false)
              }
            }}
          >
            {({
              handleChange,
              handleBlur,
              setFieldValue,
              errors,
              values,
              touched,
              isValid,
              dirty,
              isSubmitting,
              handleSubmit,
              resetForm,
              ...formik
            }) => (
              <Form>
                <Dropdown.Menu className='p-4 w-350px' style={{minHeight: '100%'}}>
                  <div className='px-4 pb-4'>
                    <div className='fs-4 text-dark fw-semibolder'>Filter</div>
                  </div>
                  <div className='separator border-gray-200'></div>
                  <div className='card px-4 mt-4'>
                    <div className='row flex-column'>
                      {currentUser?.result?.userType !== 'INTERPRETER' &&
                        currentUser?.result?.role !== 'User' && (
                          <div className='col-sm-12 col-md-12'>
                            <div className='mb-3'>
                              <div className='col-md-12'>
                                <div className='d-flex justify-content-between align-items-center'>
                                  <div>
                                    <label htmlFor='customerName' className='form-label fs-7 mb-1'>
                                      Interperter(s)
                                    </label>
                                  </div>
                                  <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={
                                        values.awsUserId_INTList?.length ===
                                        requesterOptions?.length
                                      }
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFieldValue('awsUserId_INTList', requesterOptions)
                                        } else {
                                          setFieldValue('awsUserId_INTList', [])
                                        }
                                      }}
                                    />
                                    <label className='form-check-label' htmlFor='flexRadioLg1'>
                                      All
                                    </label>
                                  </div>
                                </div>
                                  
                              </div>
                              <div className='row g-4 g-xl-4'>
                                <div className='col-sm-12 col-md-12 col-lg-12'>
                                  <div className='w-100'>
                                    <LazySelect
                                      name='awsUserId_INTList'
                                      options={!isFetchingInterpreter ? requesterOptions : []}
                                      placeholder='Select Interperter(s)'
                                      isMulti
                                      isLoading={isFetchingInterpreter}
                                      value={values.awsUserId_INTList}
                                      onChange={(e: any) => setFieldValue('awsUserId_INTList', e)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='languages' className='form-label fs-7 mb-1'>
                                  Language(s)
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values.languageList.length === languageOptions.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue('languageList', languageOptions)
                                    } else {
                                      setFieldValue('languageList', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <LazySelect
                                  name='languageList'
                                  options={!isFetchingNativeLanguage ? languageOptions : []}
                                  placeholder='Select Language(s)'
                                  isMulti
                                  isLoading={isFetchingNativeLanguage}
                                  value={values.languageList}
                                  onChange={(e: any) => setFieldValue('languageList', e)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {
                              currentUser?.result?.userType !== 'INTERPRETER' &&
                              currentUser?.result?.role !== 'User' && (
                                <div className='col-sm-12 col-md-12'>
                                  <div className='mb-3'>
                                    <div className='col-md-12'>
                                      <div className='d-flex justify-content-between align-items-center'>
                                        <div>
                                          <label htmlFor='communicationTypes' className='form-label fs-7 mb-1'>
                                            Communication Type(s)
                                          </label>
                                        </div>
                                        <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                          <input
                                            className='form-check-input'
                                            type='checkbox'
                                            checked={values.communicationTypes?.length === communicationOptions.length}
                                            onChange={(e: any) => {
                                              if (e.target.checked) {
                                                setFieldValue('communicationTypes', communicationOptions)
                                              } else {
                                                setFieldValue('communicationTypes', [])
                                              }
                                            }}
                                          />
                                          <label className='form-check-label' htmlFor='flexRadioLg1'>
                                            All
                                          </label>
                                        </div>
                                      </div>
                                    </div>

                                    <div className='row g-4 g-xl-4'>
                                      <div className='col-sm-12 col-md-12 col-lg-12'>
                                        <div className='w-100'>
                                          <LazySelect
                                            name='communicationTypes'
                                            options={!isFetchingCommunication ? communicationOptions : []}
                                            placeholder='Select Communication Type(s)'
                                            isMulti
                                            isLoading={isFetchingCommunication}
                                            value={values.communicationTypes}
                                            onChange={(e: any) => setFieldValue('communicationTypes', e)}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>)
                            }

                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='appointmentStatusList' className='form-label fs-7 mb-1'>
                                  Appointment Status
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values.appointmentStatusList?.length === appointmentStatusOptions.length}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      setFieldValue('appointmentStatusList', appointmentStatusOptions)
                                    } else {
                                      setFieldValue('appointmentStatusList', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <Select
                                  className='react-select-styled react-select-solid react-select-sm'
                                  classNamePrefix='react-select'
                                  options={appointmentStatusOptions}
                                  placeholder='Select Appointment Status'
                                  isMulti
                                  value={values.appointmentStatusList}
                                  onChange={(e: any) => setFieldValue('appointmentStatusList', e)}
                                  styles={{
                                    control: (provided: any) => ({
                                      ...provided,
                                      width: '100%',
                                      border: '1px solid #e4e6ef',
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='meetingSummaryStatusList' className='form-label fs-7 mb-1'>
                                  Meeting Summary Status
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values.meetingSummaryStatusList?.length === meetingSummaryStatusOptions.length}
                                  onChange={(e: any) => {
                                    if (e.target.checked) {
                                      setFieldValue('meetingSummaryStatusList', meetingSummaryStatusOptions)
                                    } else {
                                      setFieldValue('meetingSummaryStatusList', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <Select
                                  className='react-select-styled react-select-solid react-select-sm'
                                  classNamePrefix='react-select'
                                  options={meetingSummaryStatusOptions}
                                  placeholder='Select Meeting Summary Status'
                                  isMulti
                                  value={values.meetingSummaryStatusList}
                                  onChange={(e: any) => setFieldValue('meetingSummaryStatusList', e)}
                                  styles={{
                                    control: (provided: any) => ({
                                      ...provided,
                                      width: '100%',
                                      border: '1px solid #e4e6ef',
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                            
                      <div className='fv-row'>
                        <div className='row'>
                          <div className='col-sm-6'>
                            <label htmlFor='startDate' className='form-label fs-7 mb-1'>
                              Start Date
                            </label>
                            <input
                              type='date'
                              className='form-control form-control-white form-select-sm custom-input-height'
                              id='startDate'
                              name='startDate'
                              value={values.startDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder='Enter Start Date'
                            />
                          </div>
                          <div className='col-sm-6'>
                            <label htmlFor='endDate' className='form-label fs-7 mb-1'>
                              End Date
                            </label>
                            <input
                              type='date'
                              className='form-control form-control-white form-select-sm custom-input-height'
                              value={values.endDate}
                              id='endDate'
                              name='endDate'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder='End Date'
                              max={new Date().toISOString().split('T')[0]}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='text-end pt-5 px-4'>
                    <button
                      type='reset'
                      className='btn btn-sm btn-light me-2'
                      data-kt-menu-dismiss='true'
                      onClick={() => {
                        handleReset()
                        resetForm()
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary'
                      data-kt-menu-dismiss='true'
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting || !isValid || !dirty}
                    >
                      Apply
                    </button>
                  </div>
                </Dropdown.Menu>
              </Form>
            )}
          </Formik>
        )}
      </Dropdown>
      {isLoading && <CommonLoading />}
    </>
  )
}
